import { CARD_REGISTRY } from 'components/HomeComponent/cards/cardRegistry';
import { CAPTURE_STAT_CARD_IDS } from 'components/HomeComponent/cards/capture-stat-card-config';
import { CardId } from 'components/HomeComponent/cards/types';

const DEPRECATED_CAPTURE_CARD_ID_MAP: Record<string, CardId> = {
  capturePendingReviewFiles: 'capturePendingReview',
  capturePendingReviewTransactions: 'capturePendingReview',
  captureReviewedFiles: 'captureReadyToPost',
  captureReviewedTransactions: 'captureReadyToPost',
};

const CAPTURE_CARD_ORDER: CardId[] = [
  'captureFilesUploaded',
  'capturePendingReview',
  'captureReadyToPost',
  'capturePostedFiles',
];

const INFO_ROW_CARD_ORDER: CardId[] = [
  'tasksByStatus',
  'returnsByDueDate',
  'calendar',
];

const VALID_CARD_IDS = new Set(Object.keys(CARD_REGISTRY));

const isCaptureCardId = (cardId: CardId): boolean =>
  CAPTURE_STAT_CARD_IDS.includes(cardId);

const isInfoRowCardId = (cardId: CardId): boolean =>
  INFO_ROW_CARD_ORDER.includes(cardId);

const mapCardId = (cardId: string): CardId | null => {
  const mapped = DEPRECATED_CAPTURE_CARD_ID_MAP[cardId] ?? cardId;
  if (!VALID_CARD_IDS.has(mapped)) {
    return null;
  }
  return mapped as CardId;
};

/**
 * Desktop/PWA dashboard packing order:
 * 1) summary KPI tiles
 * 2) capture KPI tiles
 * 3) info trio (Tasks / Due Date / Calendar)
 * 4) everything else
 *
 * Without this, a saved cardOrder that lists the info trio first (or PWA
 * layout work that prepends cards) puts the large boxes at the top.
 */
export const getCanonicalDashboardCardOrder = (
  cardIds: CardId[]
): CardId[] => {
  const present = new Set(cardIds);
  const summary = cardIds.filter(
    (cardId) => CARD_REGISTRY[cardId]?.category === 'summary'
  );
  const capture = CAPTURE_CARD_ORDER.filter((cardId) => present.has(cardId));
  const info = INFO_ROW_CARD_ORDER.filter((cardId) => present.has(cardId));
  const rest = cardIds.filter(
    (cardId) =>
      CARD_REGISTRY[cardId]?.category !== 'summary' &&
      !isCaptureCardId(cardId) &&
      !isInfoRowCardId(cardId)
  );
  return [...summary, ...capture, ...info, ...rest];
};

export const migrateDashboardCardIds = (cardIds: string[]): CardId[] => {
  const seen = new Set<CardId>();
  const mapped: CardId[] = [];

  cardIds.forEach((cardId) => {
    const next = mapCardId(cardId);
    if (next && !seen.has(next)) {
      seen.add(next);
      mapped.push(next);
    }
  });

  return getCanonicalDashboardCardOrder(mapped);
};
